import { useState } from 'react';
import { CaretIcon, CheckmarkIcon } from '../assets/icons';
import type { OrgNode } from '../features/organizations/organizationsApi';

/**
 * Renders the signed-in user's org subtree (/organisation/node/my-tree/) and lets
 * them pick a booth area. Org nodes (District → Mandal → SK) are expandable; the
 * booth areas under them are the selectable leaves and carry the `area_code`.
 */
function Checkbox({ checked }: { checked: boolean }) {
  return (
    <span
      className={[
        'flex size-[18px] shrink-0 items-center justify-center rounded transition-colors',
        checked ? 'bg-brand text-white' : 'border border-[#cbd5e1] bg-white',
      ].join(' ')}
    >
      {checked && <CheckmarkIcon className="size-3.5" />}
    </span>
  );
}

export default function NodeAreaTree({
  nodes,
  value,
  onChange,
}: {
  nodes: OrgNode[];
  /** The selected booth's area_code. */
  value?: string;
  /** `nodeId` is the org node the booth sits under (for node-scoped user lookups). */
  onChange?: (areaCode: string, label: string, nodeId: number) => void;
}) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const Tick = () => (
    <span className="absolute -left-[16px] top-1/2 h-px w-3 -translate-y-1/2 bg-[#e2e8f0]" />
  );

  const renderNode = (node: OrgNode, path: string, depth: number) => {
    const nid = `${path}/${node.id}`;
    const kids = node.children ?? [];
    const areas = node.areas ?? [];
    const hasChildren = kids.length > 0 || areas.length > 0;
    const open = !collapsed.has(nid);

    return (
      <div key={nid}>
        <div className="relative">
          {depth > 0 && <Tick />}
          <div className="flex items-center gap-2 py-1.5">
            {hasChildren ? (
              <button
                type="button"
                onClick={() => toggle(nid)}
                aria-label={open ? 'Collapse' : 'Expand'}
                className="flex size-4 shrink-0 items-center justify-center text-[#64748b]"
              >
                <CaretIcon className={['size-4 transition-transform', open ? '' : '-rotate-90'].join(' ')} />
              </button>
            ) : (
              <span className="size-4 shrink-0" />
            )}
            {node.level_name && (
              <span className="shrink-0 rounded bg-[#e5eeff] px-1.5 py-0.5 text-[10px] font-medium text-brand">
                {node.level_name}
              </span>
            )}
            <button
              type="button"
              onClick={() => hasChildren && toggle(nid)}
              className="flex-1 truncate text-left text-sm font-medium text-ink"
            >
              {node.org_name}
            </button>
          </div>
        </div>

        {hasChildren && open && (
          <div className="ml-[7px] flex flex-col border-l border-[#e2e8f0] pl-[16px]">
            {kids.map((c) => renderNode(c, nid, depth + 1))}
            {areas.map((a, i) => {
              const selected = value === a.area_code;
              const label = a.booth_name || a.area_code;
              return (
                <div key={`${nid}-a-${a.id ?? i}`} className="relative">
                  <Tick />
                  <button
                    type="button"
                    onClick={() => onChange?.(a.area_code, label, node.id ?? 0)}
                    className={
                      selected
                        ? 'flex w-full items-center gap-2 rounded-lg border border-[#bfdbfe] bg-[#eff6ff] px-[13px] py-[9px] text-left'
                        : 'flex w-full items-center gap-2 py-1.5 text-left'
                    }
                  >
                    <Checkbox checked={selected} />
                    <span
                      className={[
                        'flex-1 truncate text-sm',
                        selected ? 'font-semibold text-brand' : 'font-normal text-ink-secondary',
                      ].join(' ')}
                    >
                      {label}
                    </span>
                    {selected ? (
                      <span className="shrink-0 text-[10px] font-bold uppercase tracking-[0.5px] text-brand">
                        Selected
                      </span>
                    ) : (
                      <span className="shrink-0 text-[10px] text-ink-secondary">{a.area_code}</span>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (nodes.length === 0) {
    return <p className="px-3 py-2 text-sm text-ink-secondary">No areas available.</p>;
  }

  return <div className="flex flex-col">{nodes.map((n) => renderNode(n, 'r', 0))}</div>;
}
